import React, { useState, useEffect } from 'react';
import { Search, Plus, SortAsc, SortDesc, Edit2, AlertTriangle, Settings } from 'lucide-react';
import EditConsumerDialog from './EditConsumerDialog';
import ManageIntegrationFieldsDialog from './ManageIntegrationFieldsDialog';


// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '0',
    margin: '0'
  },
  dialog: {
    backgroundColor: 'white',
    padding: '0px 20px 10px 20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    padding: '10px 0',
    color: '#000'
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const ManageConsumersDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  corporateAccount,
  currentProject,
  login_token,
  canUpdateConsumers,
  canDeleteConsumers,
  requirementIdWithPrefix,
  sourceOrProviderSystemId,
  sourceOrProviderSystemName
}) => {
  const [consumers, setConsumers] = useState([]);
  const [selectedConsumers, setSelectedConsumers] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedConsumerForEdit, setSelectedConsumerForEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredConsumers, setFilteredConsumers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Add manage fields dialog state
  const [isManageFieldsDialogOpen, setIsManageFieldsDialogOpen] = useState(false);

  // Add create consumer state variables
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [consumerDescription, setConsumerDescription] = useState('');
  const [selectedSystemId, setSelectedSystemId] = useState('');
  const [integrationSystems, setIntegrationSystems] = useState([]);
  const [integrationType, setIntegrationType] = useState('');
  const [targetDataFormat, setTargetDataFormat] = useState('');
  const [status, setStatus] = useState('Created');
  const [consumerStatuses, setConsumerStatuses] = useState([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);

  // Delete confirmation dialog state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
      margin: '0',
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px',
      cursor: 'pointer',
      userSelect: 'none',
      position: 'relative'
    },
    thNonSortable: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    checkbox: {
      marginRight: '10px',
    },
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  };

  const getIntegrationId = () => {
    if (selectedRequirements.size !== 1) return null;
    return Array.from(selectedRequirements)[0];
  };

  // Get selected consumer details for manage fields
  const getSelectedConsumerForFields = () => {
    if (selectedConsumers.size !== 1) return null;
    const selectedSystemId = Array.from(selectedConsumers)[0];
    return consumers.find(c => c.target_or_consumer_system_id === selectedSystemId);
  };

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    setConsumers([]);
    setSelectedConsumers(new Set());
    setError('');
    setSuccessMessage('');
    setIsLoading(false);
    setSearchTerm('');
    setFilteredConsumers([]);
    setCurrentPage(1);
  };

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to consumers
  const sortedConsumers = React.useMemo(() => {
    let sortableConsumers = [...filteredConsumers];
    if (sortConfig.key) {
      sortableConsumers.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableConsumers;
  }, [filteredConsumers, sortConfig]);

  // Pagination
  const totalPages = Math.ceil((sortedConsumers?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedConsumers = (sortedConsumers || []).slice(startIndex, startIndex + itemsPerPage);

  const handleEditSuccess = (success) => {
    setIsEditDialogOpen(false);
    setSelectedConsumerForEdit(null);

    if (success) {
      setSuccessMessage('Consumer updated successfully');
      fetchConsumers();
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }
  };

  const handleManageFieldsClose = () => {
    setIsManageFieldsDialogOpen(false);
  };

  // Effect to fetch consumers when dialog opens
  useEffect(() => {
    console.log('MANAGE CONSUMERS DIALOG canUpdateConsumers:', canUpdateConsumers);
    console.log('MANAGE CONSUMERS DIALOG canDeleteConsumers:', canDeleteConsumers);

    if (isOpen && selectedRequirements.size === 1) {
      resetAllData();
      setTimeout(() => {
        fetchConsumers();
      }, 100);
    }
  }, [isOpen, selectedRequirements]);

  // Search and filter effect
  useEffect(() => {
    if (searchTerm) {
      const filtered = consumers.filter(consumer =>
        consumer.system_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consumer.system_acronym.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consumer.system_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consumer.consumer_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consumer.integration_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consumer.target_data_format.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consumer.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredConsumers(filtered);
    } else {
      setFilteredConsumers(consumers);
    }
    setCurrentPage(1);
  }, [searchTerm, consumers]);

  const fetchConsumers = async () => {
    const integrationId = getIntegrationId();
    if (!integrationId) {
      setError('No integration requirement selected');
      return;
    }

    setIsLoading(true);
    setError('');
    setSelectedConsumers(new Set());

    try {
      const response = await fetch(appConfig.getUrl('/get_integration_requirement_details'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          integration_id: integrationId
        })
      });

      const data = await response.json();

      if (data.status === 'Success' && data.integration_details && data.integration_details.consumer_list) {
        setConsumers(data.integration_details.consumer_list || []);
        setFilteredConsumers(data.integration_details.consumer_list || []);
      } else if (data.status === 'Failed' && data.status_description === 'No matching consumer results found') {
        setConsumers([]);
        setFilteredConsumers([]);
      } else {
        throw new Error(data.status_description || 'Failed to fetch consumer details');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching consumers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete confirmation dialog handler
  const handleDeleteSelectedConfirm = () => {
    if (selectedConsumers.size === 0) {
      setError('No consumers selected for deletion');
      return;
    }
    setIsDeleteConfirmOpen(true);
  };

  // Actual delete function
  const handleDeleteSelected = async () => {
    setIsDeleteConfirmOpen(false);
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    const integrationId = getIntegrationId();
    const promises = [];

    for (const systemId of selectedConsumers) {
      promises.push(
        fetch(appConfig.getUrl('/delete_integration_requirement_consumer'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${login_token}`
          },
          body: JSON.stringify({
            corporate_account: corporateAccount,
            project_id: currentProject,
            integration_id: integrationId,
            target_or_consumer_system_id: systemId
          })
        })
      );
    }

    try {
      const responses = await Promise.all(promises);
      const results = await Promise.all(responses.map(r => r.json()));

      const failures = results.filter(r => r.status !== 'Success');

      if (failures.length > 0) {
        throw new Error(`Failed to delete ${failures.length} consumers`);
      }

      setSuccessMessage(`Successfully deleted ${selectedConsumers.size} consumer(s)`);
      setSelectedConsumers(new Set());

      await fetchConsumers();

      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting consumers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setIsCreateDialogOpen(true);
    fetchIntegrationSystems();
    fetchConsumerStatuses();

    setConsumerDescription('');
    setSelectedSystemId('');
    setIntegrationType('');
    setTargetDataFormat('');
    setStatus('Created');
    setError('');
    setSuccessMessage('');
  };

  const handleCreateCancel = () => {
    setIsCreateDialogOpen(false);
  };

  const fetchIntegrationSystems = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(appConfig.getUrl('/get_integration_systems_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          filter_by_status: ['Active'],
          search_query: '',
          sort_criteria: 'system_name asc'
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setIntegrationSystems(data.integration_systems_list || []);
      } else {
        throw new Error(data.status_description || 'Failed to fetch integration systems');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching integration systems:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConsumerStatuses = async () => {
    setIsLoadingStatuses(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statuses = data.entity_statuses.map(s => s.status);
        setConsumerStatuses(statuses);

        if (!status && statuses.includes('Created')) {
          setStatus('Created');
        } else if (!status && statuses.length > 0) {
          setStatus(statuses[0]);
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch statuses');
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  const handleCreateSubmit = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    if (!selectedSystemId) {
      setError('Please select a target/consumer system');
      setIsLoading(false);
      return;
    }

    const integrationId = getIntegrationId();
    if (!integrationId) {
      setError('Please select exactly one integration requirement');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/add_integration_requirement_consumer'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          integration_id: integrationId,
          target_or_consumer_system_id: selectedSystemId,
          consumer_description: consumerDescription,
          integration_type: integrationType,
          target_data_format: targetDataFormat,
          status: status
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage('Consumer created successfully');
        setIsCreateDialogOpen(false);

        await fetchConsumers();

        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to add consumer');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding consumer:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedConsumers.size === paginatedConsumers.length) {
      setSelectedConsumers(new Set());
    } else {
      const newSelected = new Set(paginatedConsumers.map(consumer => consumer.target_or_consumer_system_id));
      setSelectedConsumers(newSelected);
    }
  };

  const toggleRowSelection = (systemId) => {
    const newSelected = new Set(selectedConsumers);
    if (newSelected.has(systemId)) {
      newSelected.delete(systemId);
    } else {
      newSelected.add(systemId);
    }
    setSelectedConsumers(newSelected);
  };

  // Delete confirmation dialog component
  const renderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmOpen) return null;

    return (
      <div style={{...DialogStyles.overlay, zIndex: 1100}}>
        <div style={{...DialogStyles.dialog, width: '500px'}}>
          <div style={{...DialogStyles.title, backgroundColor: '#FFE6E6', color: '#CC0000', padding: '15px 0'}}>
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle size={20} />
              <span>Confirm Deletion</span>
            </div>
          </div>

          <div className="px-6 py-4">
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete {selectedConsumers.size} selected consumer{selectedConsumers.size !== 1 ? 's' : ''}?
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            {/* Show some details about selected consumers */}
            <div className="bg-gray-50 rounded p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-xs text-gray-600 mb-2">Selected consumers:</p>
              {Array.from(selectedConsumers).slice(0, 5).map(systemId => {
                const consumer = consumers.find(c => c.target_or_consumer_system_id === systemId);
                return consumer ? (
                  <div key={systemId} className="text-xs text-gray-700 mb-1">
                    {consumer.system_name} ({consumer.system_acronym}) - {consumer.consumer_description || 'No description'}
                  </div>
                ) : null;
              })}
              {selectedConsumers.size > 5 && (
                <div className="text-xs text-gray-500">
                  ... and {selectedConsumers.size - 5} more
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-center gap-4 p-6 pt-0">
            <button
              onClick={() => setIsDeleteConfirmOpen(false)}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSelected}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Delete {selectedConsumers.size} Consumer{selectedConsumers.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateDialog = () => {
    if (!isCreateDialogOpen) return null;

    return (
      <div style={DialogStyles.overlay}>
        <div style={{...DialogStyles.dialog, width: '700px'}}>
          <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF', height: 40 }}>Add Consumer</h2>

          <div className="mb-6 p-4 border border-gray-300 rounded-md">
            <h3 className="text-base font-semibold mb-3">Adding consumer to: <strong>{requirementIdWithPrefix || 'No requirement selected'}</strong></h3>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Target/Consumer System: <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedSystemId}
              onChange={(e) => setSelectedSystemId(e.target.value)}
              className="w-full p-2 border rounded"
              required
              disabled={selectedRequirements.size !== 1}
            >
              <option value="">Select a Target/Consumer System</option>
              {integrationSystems.map(system => (
                <option key={system.system_id} value={system.system_id}>
                  {system.system_name} ({system.system_acronym}) - {system.system_description}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Consumer Description:
            </label>
            <textarea
              value={consumerDescription}
              onChange={(e) => setConsumerDescription(e.target.value)}
              className="w-full p-2 border rounded"
              rows={3}
              placeholder="Describe how this system consumes the integration"
              disabled={selectedRequirements.size !== 1}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Integration Type:
            </label>
            <select
              value={integrationType}
              onChange={(e) => setIntegrationType(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={selectedRequirements.size !== 1}
            >
              <option value="">Select integration type</option>
              <option value="Sync">Synchronous</option>
              <option value="Async">Asynchronous</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Target Data Format:
            </label>
            <select
              value={targetDataFormat}
              onChange={(e) => setTargetDataFormat(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={selectedRequirements.size !== 1}
            >
              <option value="">Select data format</option>
              <option value="JSON">JSON</option>
              <option value="XML">XML</option>
              <option value="CSV">CSV</option>
              <option value="Binary">Binary</option>
              <option value="EDI">EDI</option>
              <option value="Flat File">Flat File</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Status:
            </label>
            {isLoadingStatuses ? (
              <div className="w-full p-2 bg-gray-100 rounded text-gray-500">Loading statuses...</div>
            ) : (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full p-2 border rounded"
                disabled={selectedRequirements.size !== 1}
              >
                <option value="">Select status</option>
                {consumerStatuses.map(statusOption => (
                  <option key={statusOption} value={statusOption}>
                    {statusOption}
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && (
            <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
              {error}
            </div>
          )}

          <div className="flex justify-center gap-4 mt-6">
            <button
              onClick={handleCreateCancel}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={isLoading}
            >
              Close
            </button>
            <button
              onClick={handleCreateSubmit}
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={isLoading || selectedRequirements.size !== 1}
            >
              {isLoading ? 'Creating...' : 'Create Consumer'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={DialogStyles.overlay}>
        <div style={{
          ...DialogStyles.dialog,
          width: '1500px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          gap: '0',
          margin: '0'
        }}>

          {/* Header */}
          <div className="px-6 pt-0 pb-0">
            <div className="flex items-center mb-4 mt-0 justify-between bg-[#CCCCFF] p-3 rounded">
              <h1 style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'black',
                margin: 0
              }}>
                Manage Consumers - {requirementIdWithPrefix || 'No requirement selected'}
              </h1>

              <div className="flex gap-2">
                <div className="group relative">
                  <button
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                    onClick={() => setIsManageFieldsDialogOpen(true)}
                    disabled={!getIntegrationId() || !canUpdateConsumers || selectedConsumers.size !== 1}
                  >
                    <Settings size={18} className="mr-2" />
                    Manage Consumer Fields
                  </button>

                  {/* Manage Fields button tooltip */}
                  {(!getIntegrationId() || !canUpdateConsumers || selectedConsumers.size !== 1) && (
                    <div className="absolute right-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      {!getIntegrationId()
                        ? "Select one integration requirement"
                        : !canUpdateConsumers
                        ? "Insufficient permission"
                        : "Select exactly one consumer"
                      }
                      <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
                    </div>
                  )}
                </div>

                <div className="group relative">
                  <button
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    onClick={openCreateDialog}
                    disabled={!getIntegrationId() || !canUpdateConsumers}
                  >
                    <Plus size={18} className="mr-2" />
                    Add New Consumer
                  </button>

                  {/* Add Consumer button tooltip */}
                  {(!getIntegrationId() || !canUpdateConsumers) && (
                    <div className="absolute right-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      {!getIntegrationId()
                        ? "Select one integration requirement"
                        : "Insufficient permission"
                      }
                      <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          <div className="px-6">
            {selectedRequirements.size !== 1 && (
              <div className="mb-4 p-2 rounded bg-yellow-50 text-yellow-700">
                Please select exactly one integration requirement to view its consumers
              </div>
            )}

            {error && (
              <div className={`mb-4 p-2 rounded ${
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              }`}>
                {error}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
                {successMessage}
              </div>
            )}

            {consumers.length === 0 && !isLoading && !error && selectedRequirements.size === 1 && (
              <div className="mb-4 p-2 rounded bg-yellow-50 text-yellow-700">
                No consumers found for this integration requirement
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto px-6">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-2 text-left">
                    <input
                      type="checkbox"
                      checked={consumers.length > 0 && selectedConsumers.size === consumers.length}
                      onChange={(e) => {
                        const newSelected = new Set();
                        if (e.target.checked) {
                          consumers.forEach(consumer => newSelected.add(consumer.target_or_consumer_system_id));
                        }
                        setSelectedConsumers(newSelected);
                      }}
                      disabled={consumers.length === 0}
                    />
                  </th>
                  <th className="p-2 text-left">System Name</th>
                  <th className="p-2 text-left">System Acronym</th>
                  <th className="p-2 text-left">System Description</th>
                  <th className="p-2 text-left">Consumer Description</th>
                  <th className="p-2 text-left">Integration Type</th>
                  <th className="p-2 text-left">Target Data Format</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8">Loading...</td>
                  </tr>
                ) : consumers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-gray-500">
                      No consumers found for this integration requirement.
                    </td>
                  </tr>
                ) : (
                  consumers.map(consumer => (
                    <tr key={consumer.target_or_consumer_system_id} className="border-t hover:bg-gray-50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedConsumers.has(consumer.target_or_consumer_system_id)}
                          onChange={() => {
                            const newSelected = new Set(selectedConsumers);
                            if (newSelected.has(consumer.target_or_consumer_system_id)) {
                              newSelected.delete(consumer.target_or_consumer_system_id);
                            } else {
                              newSelected.add(consumer.target_or_consumer_system_id);
                            }
                            setSelectedConsumers(newSelected);
                          }}
                        />
                      </td>
                      <td className="p-2">{consumer.system_name}</td>
                      <td className="p-2">{consumer.system_acronym}</td>
                      <td className="p-2">{consumer.system_description}</td>
                      <td className="p-2">{consumer.consumer_description}</td>
                      <td className="p-2">{consumer.integration_type}</td>
                      <td className="p-2">{consumer.target_data_format}</td>

                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          consumer.status === 'Active' ? 'bg-green-100 text-green-800' :
                          consumer.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          consumer.status === 'Inactive' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {consumer.status}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <button
                          className="p-1 rounded hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center"
                          onClick={() => {
                            setSelectedConsumerForEdit(consumer);
                            setIsEditDialogOpen(true);
                          }}
                          disabled={!canUpdateConsumers}
                          title="Edit Consumer"
                        >
                          <Edit2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {!isLoading && sortedConsumers.length > 0 && (
            <div className="mx-6 mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Total Entries: {sortedConsumers.length}</span>
                <span>| Showing {sortedConsumers.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedConsumers.length)} of {sortedConsumers.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded p-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}

          {/* Footer with action buttons and tooltips */}
          <div className="p-6 pt-4 border-t">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <div className="group relative">
                  <button
                    style={{
                      ...styles.deleteButton,
                      padding: '8px 16px',
                      backgroundColor: (isLoading || selectedConsumers.size === 0 || !canDeleteConsumers) ? '#FF8F89' : '#dc3545',
                      opacity: (isLoading || selectedConsumers.size === 0 || !canDeleteConsumers) ? 0.6 : 1,
                      cursor: 'pointer'
                    }}
                    onClick={handleDeleteSelectedConfirm}
                    disabled={isLoading || selectedConsumers.size === 0 || !canDeleteConsumers}
                  >
                    {isLoading ? 'Removing...' : `Remove Selected (${selectedConsumers.size})`}
                  </button>

                  {/* Delete button tooltip */}
                  {(selectedConsumers.size === 0 || !canDeleteConsumers) && (
                    <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      {selectedConsumers.size === 0
                        ? "Select one or more consumers to delete"
                        : "Insufficient permission"
                      }
                      <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedConsumers.size > 0 ? `${selectedConsumers.size} selected` : ''}
                </span>
                <button
                  style={{
                    backgroundColor: '#FFFDD0',
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Consumer Dialog */}
        <EditConsumerDialog
          isOpen={isEditDialogOpen}
          onClose={handleEditSuccess}
          selectedRequirements={selectedRequirements}
          consumer={selectedConsumerForEdit}
          corporateAccount={corporateAccount}
          currentProject={currentProject}
          login_token={login_token}
        />

        {/* Manage Consumer Fields Dialog */}
        {isManageFieldsDialogOpen && getSelectedConsumerForFields() && (
          <ManageIntegrationFieldsDialog
            isOpen={isManageFieldsDialogOpen}
            onClose={handleManageFieldsClose}
            selectedRequirements={selectedRequirements}
            corporateAccount={corporateAccount}
            currentProject={currentProject}
            login_token={login_token}
            canUpdateIntegrationFields={canUpdateConsumers}
            canDeleteIntegrationFields={canDeleteConsumers}
            requirementIdWithPrefix={requirementIdWithPrefix}
            systemId={getSelectedConsumerForFields().target_or_consumer_system_id}
            systemType="CONSUMER"
            systemName={getSelectedConsumerForFields().system_name}
            sourceOrProviderSystemId={sourceOrProviderSystemId}
            sourceOrProviderSystemName={sourceOrProviderSystemName}
          />
        )}

        {/* Create Consumer Dialog */}
        {renderCreateDialog()}

        {/* Delete Confirmation Dialog */}
        {renderDeleteConfirmDialog()}
      </div>
    </div>
  );
};

export default ManageConsumersDialog;